import { useMemo, useRef, useState } from 'react';

import { LoggerHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { Box, CSSProperties } from '@bcp-nextgen-dx-component-factory/design-system';
import { AppHelper } from '@helpers';
import { useLoader } from '@hooks';
import { BlockStepManager } from '@modules';
import { LoginBlock, useCookiesStore, useLoginStore, useServicesStore } from '@store';
import { SafeCredentialTypeEnum } from '@types';

import { AliasBlock, MultichannelBlock, PasswordBlock, SafeBlock, TaxNumberBlock } from './blocks';
import { FormHidden } from './formHidden';

export interface LoginFormProps {
	styles?: CSSProperties;
	onAliasClosedContentLinkClick?: () => void;
	onAliasOpenContentButtonClick?: () => void;
}

export const LoginForm = ({ styles, onAliasClosedContentLinkClick, onAliasOpenContentButtonClick }: LoginFormProps) => {
	const globalLoader = useLoader('global');

	const accountChallengeErrorCode = useServicesStore((state) => state.accountChallenge?.errorCode);
	const responseTypeCookie = useCookiesStore((state) => state.responseType);
	const flow = useLoginStore((state) => state.flow);

	const submitBtnRef = useRef<HTMLButtonElement>(null);

	const [submitAction, setSubmitAction] = useState({
		withCertificate: false,
		safeCredentialType: 0,
	});

	const defaultBlocksState = useMemo(() => {
		if (
			responseTypeCookie === 'serviceUnavailable' ||
			responseTypeCookie === 'invalidCredentials' ||
			responseTypeCookie === 'invalidCaptcha'
		) {
			if (responseTypeCookie !== 'serviceUnavailable' && AppHelper.isSessionExpired(accountChallengeErrorCode)) {
				return [{ name: LoginBlock.Alias, isOpen: true }];
			}

			switch (flow) {
				case 'multichannel':
					return [
						{ name: LoginBlock.Alias, isOpen: false },
						{ name: LoginBlock.Multichannel, isOpen: true },
					];
				case 'multichannelWithSafe':
					return [
						{ name: LoginBlock.Alias, isOpen: false },
						{ name: LoginBlock.Multichannel, isOpen: true },
						{ name: LoginBlock.Safe, isOpen: true },
					];
				case 'password':
					return [
						{ name: LoginBlock.Alias, isOpen: false },
						{ name: LoginBlock.Password, isOpen: true },
					];
				case 'passwordWithTaxNumber':
					return [
						{ name: LoginBlock.Alias, isOpen: false },
						{ name: LoginBlock.Password, isOpen: true },
						{ name: LoginBlock.TaxNumber, isOpen: true },
					];
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSubmitClick = (withCertificate?: boolean, safeCredentialType?: SafeCredentialTypeEnum) => {
		LoggerHelper.log('App', 'LoginForm > handleSubmitClick:', { withCertificate, safeCredentialType });

		globalLoader.show();

		if (withCertificate) {
			setSubmitAction((current) => ({
				...current,
				withCertificate: true,
			}));
		} else if (safeCredentialType) {
			setSubmitAction((current) => ({
				...current,
				safeCredentialType,
			}));
		} else {
			submitBtnRef.current?.click();
		}
	};

	return (
		<>
			<form onSubmit={(e) => e.preventDefault()} autoComplete="off" style={{ width: '100%' }}>
				<Box styles={{ flexDirection: 'column', ...styles }}>
					<BlockStepManager
						withoutProvider
						defaultBlocksState={defaultBlocksState}
						blocks={[
							{
								name: LoginBlock.Alias,
								render: ({ isOpen }) => (
									<AliasBlock
										isActive={isOpen}
										onClosedContentLinkClick={onAliasClosedContentLinkClick}
										onOpenContentButtonClick={onAliasOpenContentButtonClick}
									/>
								),
							},
							{
								name: LoginBlock.Multichannel,
								render: ({ isOpen }) => (
									<MultichannelBlock isActive={isOpen} onSubmit={handleSubmitClick} />
								),
							},
							{
								name: LoginBlock.Password,
								render: ({ isOpen }) => <PasswordBlock isActive={isOpen} />,
							},
							{
								name: LoginBlock.TaxNumber,
								render: ({ isOpen }) => (
									<TaxNumberBlock isActive={isOpen} onSubmit={handleSubmitClick} />
								),
							},
							{
								name: LoginBlock.Safe,
								render: ({ isOpen }) => <SafeBlock isActive={isOpen} onSubmit={handleSubmitClick} />,
							},
						]}
					/>
				</Box>
			</form>

			<FormHidden
				submitBtnRef={submitBtnRef}
				withCertificate={submitAction.withCertificate}
				safeCredentialType={submitAction.safeCredentialType}
			/>
		</>
	);
};
